import { Card, Divider, GhostIconButton, mq } from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BasicButton, Text } from "@certn/cascadia";
import { css } from "@emotion/css";
import { components } from "@certn/api/generated-schemas/client-portal";
import { formatDate } from "@certn/utils";
import { PreviewRubricModal } from "../../../components/preview-adjudication-rubric/PreviewRubricModal";
import { ParentEmploymentVerificationAdjudicationResults } from "./ParentEmploymentVerificationAdjudicationResults";
import { ChildEmploymentVerificationAdjudicationResults } from "./ChildEmploymentVerificationAdjudicationResults";
import { BasicCanadianCriminalRecordAdjudicationResults } from "./BasicCanadianCriminalRecordAdjudicationResults";
import {
  isChildEmploymentVerification,
  isParentEmploymentVerification,
} from "../../adjudication/utils";
import {
  BASIC_CANADIAN_CRIMINAL_RECORD_CHECK_TYPES,
  CHILD_EMPLOYMENT_CHECK_TYPES,
  PARENT_EMPLOYMENT_CHECK_TYPES,
} from "../../adjudication/constants";
import { CheckScoreTag } from "@certn/report/components/check-score-tag/CheckScoreTag";

interface AdjudicationCheckCardProps {
  isChildCheck?: boolean;
  check: components["schemas"]["AdjudicationCaseResultsCheck"];
}

export function AdjudicationCheckCard({
  isChildCheck = false,
  check,
}: AdjudicationCheckCardProps) {
  const { t } = useTranslation();
  const [openResult, setOpenResult] = useState(
    check.child_checks && check.child_checks.length > 0
  );
  const { adjudication, adjudication_results } = check;
  const styles = getCheckCardStyles(isChildCheck);

  const hasChildren = check.child_checks && check.child_checks.length > 0;
  const showExpandIcon =
    !hasChildren &&
    (Boolean(adjudication_results) || Boolean(adjudication?.criteria));

  const hasAdjudicationData =
    Boolean(adjudication_results) || Boolean(adjudication?.criteria);

  const hasChildrenWithAdjudication =
    hasChildren &&
    check.child_checks?.some((childCheck) => {
      return (
        Boolean(childCheck.adjudication_results) ||
        Boolean(childCheck.adjudication?.criteria)
      );
    });

  if (!hasAdjudicationData && !hasChildrenWithAdjudication) {
    return null;
  }

  const renderAdjudicationResults = () => {
    if (!adjudication_results) return null;

    if (BASIC_CANADIAN_CRIMINAL_RECORD_CHECK_TYPES.includes(check.type)) {
      return (
        <BasicCanadianCriminalRecordAdjudicationResults
          adjudicationResults={adjudication_results}
        />
      );
    }
    if (PARENT_EMPLOYMENT_CHECK_TYPES.includes(check.type)) {
      return (
        <ParentEmploymentVerificationAdjudicationResults
          adjudicationResults={adjudication_results}
        />
      );
    }
    if (CHILD_EMPLOYMENT_CHECK_TYPES.includes(check.type)) {
      return (
        <ChildEmploymentVerificationAdjudicationResults
          adjudicationResults={adjudication_results}
        />
      );
    }
    return null;
  };

  return (
    <Card
      hideBorder={isChildCheck}
      type="large"
      ariaLabel={t("{{name}} Adjudication Details", {
        name: check.type_display,
      })}
      className={styles.cardContainer}
      testID="adjudication-check-card"
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <Text style="global/heading-large" testID="check-title">
            {check.type_display}
          </Text>
          {showExpandIcon && (
            <GhostIconButton
              icon={!openResult ? "expand_more" : "expand_less"}
              testID={
                isChildCheck
                  ? "adjudication-check-card-expand-child"
                  : "adjudication-check-card-expand"
              }
              onPress={() => {
                setOpenResult(!openResult);
              }}
            />
          )}
        </div>
        <div className={styles.checkResult}>
          <CheckScoreTag
            score={check.adjudication_score ?? check.score}
            subScore={check.adjudication_sub_score ?? check.sub_score}
          />
          {check.completed_at && (
            <div aria-label={t("Check completed date")}>
              <Text style="form/body-secondary">
                {t("Completed {{date}}", {
                  date: formatDate(check.completed_at, "PP"),
                })}
              </Text>
            </div>
          )}
          {isParentEmploymentVerification(check.type) &&
            check.child_checks &&
            check.child_checks.length > 0 && (
              <div className={styles.checkResult}>
                <span className={styles.dot}>&middot;</span>
                <Text style="form/body-secondary">
                  {t("{{count}} verification{{plural}}", {
                    count: check.child_checks.length,
                    plural: check.child_checks.length !== 1 ? "s" : "",
                  })}
                </Text>
              </div>
            )}
        </div>
      </div>

      {openResult && (
        <div className={styles.openResult}>
          {Boolean(adjudication?.criteria) &&
            !isChildEmploymentVerification(check.type) && (
              <PreviewRubricModal
                rulesets={[
                  {
                    check_type: check.type,
                    criteria: adjudication?.criteria,
                    id: adjudication?.id || "",
                  },
                ]}
                triggerComponent={
                  <BasicButton testID={"preview-adjudication-rubric-button"}>
                    {t("Preview adjudication rubric")}
                  </BasicButton>
                }
              />
            )}
          {Boolean(adjudication?.criteria) && (
            <>
              {adjudication_results ? (
                <Text style="form/body-primary">
                  {t(
                    "Adjusted scores reflect how the rubric has modified the original report results. Only differences are shown here."
                  )}
                </Text>
              ) : (
                <Text style="form/body-primary">
                  {t(
                    "No adjudication changes were applied. Report results remain unchanged."
                  )}
                </Text>
              )}
              {renderAdjudicationResults()}
            </>
          )}
        </div>
      )}

      {hasChildren &&
        check.child_checks?.map((childCheck, index) => (
          <div key={`${childCheck.id}-child-${index}`}>
            <Divider />
            <AdjudicationCheckCard check={childCheck} isChildCheck={true} />
          </div>
        ))}
    </Card>
  );
}

const getCheckCardStyles = (isChildCheck: boolean) => {
  return {
    cardContainer: css({
      marginBottom: "var(--space-4)",
      background: isChildCheck ? "none" : "var(--background-base)",
    }),
    container: css(
      mq({
        margin: "var(--space-4)",
      })
    ),
    header: css({
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }),
    dot: css({ lineHeight: "100%" }),
    checkResult: css({
      display: "flex",
      alignItems: "center",
      gap: "var(--space-2)",
      flexWrap: "wrap",
    }),
    openResult: css(
      mq({
        margin: ["var(--space-4)"],
        borderTop: "solid 1px var(--border-base)",
        paddingTop: "var(--space-4)",
        display: "grid",
        gap: "var(--space-6)",
      })
    ),
  };
};
